import { useState } from "react";
import { Button, Field, Input, Label, Switch } from "@headlessui/react";
import { useAppSelector } from "../../../../app/hooks";
import { UserRole } from "../../../../app/userSlice";
import RequiredField from "../../../../components/RequiredField";
import CleverIcon from "../../../../assets/CleverIcon";
import ClassLinkIcon from "../../../../assets/ClassLinkIcon";
import DistrictCleverSettings from "../settings/cleverClassLinkSettings/DistrictCleverSettings";
import DistrictClassLinkSettings from "../settings/cleverClassLinkSettings/DistrictClassLinkSettings";

export interface PrimaryDistrictInfoSectionProps {
	value: {
		districtId?: number;
		name: string;
		code: string;
		externalID: string;
		cleverId?: string;
		showCleverId?: boolean;
		classLinkId?: string;
		showClassLinkId?: boolean;
		disableUserImages?: boolean;
		isIncludedInReports?: boolean;
	};
	onPrimaryInfoChange: (
		patch: Partial<{
			name: string;
			code: string;
			externalID: string;
		}>
	) => void;
	onAddSettingsInfoChange?: (
		patch: Partial<{
			disableUserImages: boolean;
			isIncludedInReports: boolean;
		}>
	) => void;
	onCleverClassLinkChange?: (
		patch: Partial<{
			cleverId: string;
			classLinkId: string;
		}>
	) => void;
	editMode?: boolean;
}

export default function PrimaryDistrictInfoSection({
	value,
	onPrimaryInfoChange,
	onAddSettingsInfoChange,
	onCleverClassLinkChange,
	editMode,
}: PrimaryDistrictInfoSectionProps) {
	const { role } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);
	const [openCleverClassLinkSettings, setOpenCleverClassLinkSettings] = useState(false);
	const [showCleverForm, setShowCleverForm] = useState(false);
	const [showUserAvatars, setShowUserAvatars] = useState(!value.disableUserImages);

	function toggleShowUserAvatars(val: boolean) {
		// Display to keep input logic "positive".
		setShowUserAvatars(val);
		// Actual to store negatively flipped value.
		onAddSettingsInfoChange!({ disableUserImages: !val });
	}

	// Determine which fields to show
	const showOutsideSite = value.showCleverId || value.showClassLinkId;
	const hasClever = !!value.cleverId;
	const hasClassLink = !!value.classLinkId;
	const showBoth = !hasClever && !hasClassLink;
	const showCleverOnly = hasClever && !hasClassLink;
	const showClassLinkOnly = !hasClever && hasClassLink;

	return (
		<div
			className={`${editMode ? "mt-6" : "!border-transparent mt-2"} flex flex-col gap-x-12 w-full`}>
			<div className="flex justify-between">
				<h2 className="mb-2">District Information</h2>
				<p className="text-xs leading-6 text-gray-600">
					<RequiredField className="!text-sm" /> - Denotes a required field
				</p>
			</div>
			<div
				className={`${editMode ? "grid-cols-1 gap-y-3 sm:grid-cols-6" : "gap-y-2 grid-cols-6"} grid gap-x-6`}>
				<Field className="col-span-3">
					<Label htmlFor="districtName" className="font-medium text-sm">
						Name
						<RequiredField />
					</Label>
					<Input
						id="districtName"
						name="districtName"
						type="text"
						required
						maxLength={255}
						className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						value={value.name ?? ""}
						onChange={(e) => {
							onPrimaryInfoChange({ name: e.target.value });
						}}
					/>
				</Field>
				<Field className="col-span-3">
					<Label htmlFor="districtCode" className="font-medium text-sm">
						District Code
						<RequiredField />
					</Label>
					<Input
						id="districtCode"
						name="districtCode"
						type="text"
						required
						maxLength={15}
						className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						value={value.code ?? ""}
						onChange={(e) => {
							onPrimaryInfoChange({ code: e.target.value });
						}}
					/>
				</Field>
				<Field className={"col-span-3"}>
					<Label htmlFor="externalId" className="font-medium text-sm">
						External ID
					</Label>
					<Input
						id="externalId"
						name="externalId"
						type="text"
						maxLength={40}
						className="block w-full mt-1 rounded-lg border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						value={value.externalID ?? ""}
						onChange={(e) => {
							onPrimaryInfoChange({ externalID: e.target.value });
						}}
					/>
				</Field>
				{!editMode && (
					<div className="col-span-3 flex flex-col justify-between">
						{!config?.isUtahComposeSite && (
							<Field className="flex items-center justify-start gap-4">
								<Label className="font-medium text-sm">Allow User Avatars:</Label>
								<Switch
									checked={showUserAvatars}
									onChange={(v) => {
										toggleShowUserAvatars(v);
									}}
									name="userAvatar"
									className="group relative inline-flex h-6 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
									<span
										aria-hidden="true"
										className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
									/>
								</Switch>
							</Field>
						)}
						<Field className="flex items-center justify-start gap-4">
							<Label className="font-medium text-sm">Include In Reports:</Label>
							<Switch
								checked={value.isIncludedInReports}
								onChange={(v) =>
									onAddSettingsInfoChange!({ isIncludedInReports: v })
								}
								name="includeInReports"
								className="group relative inline-flex h-6 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
								<span
									aria-hidden="true"
									className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
								/>
							</Switch>
						</Field>
					</div>
				)}
				{role === UserRole.Admin && showOutsideSite && editMode && (
					<div className="flex flex-col sm:flex-row gap-3 md:gap-4 col-span-3">
						{(showBoth || showCleverOnly) && (
							<div className="flex items-end w-full">
								<Field className="w-full">
									<Label
										htmlFor="cleverId-readonly"
										className="font-medium text-sm">
										Clever ID
									</Label>
									<Input
										id="cleverId-readonly"
										name="cleverId-readonly"
										type="text"
										readOnly
										className="block w-full mt-1 rounded-lg rounded-r-none border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={value.cleverId ?? ""}
									/>
								</Field>
								<Button
									className="rounded-lg rounded-l-none border-0 ring-1 ring-inset ring-gray-900/20 cursor-pointer flex justify-center overflow-hidden"
									onClick={() => {
										setShowCleverForm(true);
										setOpenCleverClassLinkSettings(true);
									}}>
									<CleverIcon className="h-9 w-10 p-1 fill-white bg-[#1464FF]" />
								</Button>
							</div>
						)}
						{(showBoth || showClassLinkOnly) && (
							<div className="flex items-end w-full">
								<Field className="w-full">
									<Label
										htmlFor="classLinkId-readonly"
										className="font-medium text-sm">
										ClassLink ID
									</Label>
									<Input
										id="classLinkId-readonly"
										name="classLinkId-readonly"
										type="text"
										readOnly
										className="block w-full mt-1 rounded-lg rounded-r-none border-0 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 read-only:bg-slate-200/75 read-only:focus:ring-gray-300 read-only:focus:ring-1 focus:ring-2 focus:ring-inset focus:ring-primary-600"
										value={value.classLinkId ?? ""}
									/>
								</Field>
								<Button
									className="rounded-lg rounded-l-none border-0 ring-1 ring-inset ring-gray-900/20 cursor-pointer flex justify-center overflow-hidden"
									onClick={() => {
										setShowCleverForm(false);
										setOpenCleverClassLinkSettings(true);
									}}>
									<ClassLinkIcon className="h-9 w-10 p-1 fill-white bg-[#0a4d7f]" />
								</Button>
							</div>
						)}
					</div>
				)}
				{openCleverClassLinkSettings && showCleverForm && (
					<DistrictCleverSettings
						districtId={value.districtId!}
						isOutsideSiteFormOpen={openCleverClassLinkSettings}
						setIsOutsideSiteFormOpen={setOpenCleverClassLinkSettings}
						onChange={onCleverClassLinkChange!}
					/>
				)}
				{openCleverClassLinkSettings && !showCleverForm && (
					<DistrictClassLinkSettings
						districtId={value.districtId!}
						isOutsideSiteFormOpen={openCleverClassLinkSettings}
						setIsOutsideSiteFormOpen={setOpenCleverClassLinkSettings}
						onChange={onCleverClassLinkChange!}
					/>
				)}
			</div>
		</div>
	);
}
